import { ChevronLeft, XClose } from "@untitled-ui/icons-react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";

import Button from "@/components/forms/Button";
import Checkbox from "@/components/forms/Checkbox";
import Combobox from "@/components/forms/list/combobox/Combobox";
import ComboboxInput from "@/components/forms/list/combobox/ComboboxInput";
import ComboboxOption from "@/components/forms/list/combobox/ComboboxOption";
import Loading from "@/components/loadings/Loading";
import UserAvatar from "@/components/media/UserAvatar";
import { type User } from "@/types";
import type { useFetchUsersInfinite } from "@/user/services";
import { getNameGroupLabel } from "@/utils";

type MembersPageProps = {
  selectedUsers: User<true>[];
  selectedUserIds: Set<number>;
  users: ReturnType<typeof useFetchUsersInfinite>["data"];
  searchUsers?: User<true>[];
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  onBack: () => void;
  onSearchChange: (value: string) => void;
  onToggleUser: (user: User<true>) => void;
  onRemoveUser: (user: User<true>) => void;
};

export default function MembersPage({
  selectedUsers,
  selectedUserIds,
  users,
  searchUsers,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  onBack,
  onSearchChange,
  onToggleUser,
  onRemoveUser,
}: MembersPageProps) {
  const { t } = useTranslation("messenger.user");
  let prevLetter = "";

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="px-4 pb-4">
        <div className="flex items-center py-4">
          <Button color="tertiary-gray" onClick={onBack}>
            <ChevronLeft className="size-5 text-gray-700 rtl:rotate-180" />
          </Button>
          <span className="text-lg font-semibold text-gray-900">
            {t("Select Group Members")}
          </span>
        </div>
        <div className="pb-2">
          <Combobox
            className="w-full"
            options={searchUsers}
            placeholder={t("Who do you want to add?")}
            value={null}
            hideChevron
            by="id"
            onChange={(selectedUser) => {
              if (selectedUser) {
                onToggleUser(selectedUser);
              }
            }}
            renderItem={({ item }) => (
              <ComboboxOption value={item}>
                <div className="flex items-center gap-x-2">
                  <UserAvatar user={item} size="xs" />
                  <span className="font-medium">
                    {item.first_name} {item.last_name}
                  </span>
                </div>
              </ComboboxOption>
            )}
          >
            <ComboboxInput
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={t("Search", { ns: "core.shared" })}
            />
          </Combobox>
        </div>
        {(selectedUsers ?? []).length > 0 && (
          <div className="flex flex-wrap items-center gap-2 pb-4">
            {selectedUsers.map((user) => (
              <span
                key={user.id}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 py-px ps-2.5 pe-2 text-sm text-gray-700"
              >
                <UserAvatar size="xs" user={user} />
                {user.first_name} {user.last_name}
                <button
                  type="button"
                  className="ms-1 cursor-pointer text-gray-400"
                  onClick={() => onRemoveUser(user)}
                >
                  <XClose className="size-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <InfiniteScroll
          pageStart={0}
          loadMore={() => !isFetchingNextPage && fetchNextPage()}
          hasMore={hasNextPage}
          useWindow={false}
          className="flex flex-col"
          loader={<Loading key={0} className="my-2 self-center" size="sm" />}
        >
          {users?.pages?.map((page) =>
            page?.data?.map((user: User<true>) => {
              const letter = getNameGroupLabel(user.first_name?.[0] ?? "#");
              const showHeader = letter !== prevLetter;
              const checked = selectedUserIds.has(user.id);
              prevLetter = letter;

              return (
                <div key={user.id}>
                  {showHeader && (
                    <div className="bg-gray-50 px-10 py-2 text-sm/5 font-medium text-gray-600">
                      {letter}
                    </div>
                  )}
                  <div
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-4 py-2 hover:bg-gray-50"
                    onClick={() => onToggleUser(user)}
                  >
                    <UserAvatar size="lg" user={user} disableTooltip />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-gray-700">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="truncate text-sm text-gray-400">
                        {user.position?.name || "-"}
                      </div>
                    </div>
                    <Checkbox
                      onClick={() => onToggleUser(user)}
                      checked={checked}
                      size="sm"
                    />
                  </div>
                </div>
              );
            }),
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
}
